import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { NewsService } from '../../../shared/services/news/news.service';
import { DeleteComponent } from '../../../shared/components/delete/delete.component';
import { CustomPipe } from '../../../shared/pipes/custom.pipe';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { MailTitlePipe } from '../../../shared/pipes/mail-title.pipe';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { UserProfile } from '../../../shared/interfaces/role.interface';
import { QeqPipe } from 'src/app/shared/pipes/qeq-pipe/qeq-pippe.pipe';

@Component({
  selector: 'app-culture',
  imports: [
    MatIcon,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    CommonModule,
    MatDialogModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTabsModule,
    MatDatepickerModule,
    MatInputModule,
    CustomPipe,
    TitleComponent,
    MailTitlePipe,
    RouterOutlet,
    LoaderComponent,
    QeqPipe,
  ],
  templateUrl: './culture.component.html',
  styleUrls: ['./culture.component.css', '../../../shared/styles/layout.css'],
})
export class CultureComponent implements OnDestroy {
  title: string = 'Cultura empresarial';
  subtitle: string = 'Gestiona los apartados de cultura empresarial.';

  // Store subscriptions for cleanup
  private subscriptions: Subscription[] = [];
  private navigationSubscription: Subscription | null = null;

  get isLoading() {
    return this.loaderService.getLoader();
  }

  constructor(
    private newsService: NewsService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private rolesService: RolesService,
    private loaderService: LoaderService,
    private storageService: StorageService
  ) {}

  selectedFilter: string = 'todo';

  filters = [
    { value: 'mes', label: 'Mes' },
    { value: 'semana', label: 'Últimos 7 días' },
    { value: 'hoy', label: 'Hoy' },
    { value: 'todo', label: 'Todo' },
  ];

  selectFilter(filter: string) {
    this.selectedFilter = filter;
    this.applyFilters();
  }

  // Helper methods for date filtering
  private getStartDateForFilter(filter: string): Date | null {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today

    switch (filter) {
      case 'mes':
        const monthStart = new Date(today);
        monthStart.setDate(1); // First day of current month
        return monthStart;

      case 'semana':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - 6); // 7 days ago (including today)
        return weekStart;

      case 'hoy':
        return today;

      case 'todo':
      default:
        return null; // No date filtering
    }
  }

  // Method to apply filters
  applyFilters() {
    const startDate = this.getStartDateForFilter(this.selectedFilter);

    const filterValue = JSON.stringify({
      dateFilter: startDate ? startDate.getTime() : null, // Convert to timestamp for comparison
    });

    this.dataSource.filter = filterValue;
  }

  readonly dialog = inject(MatDialog);

  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  private _liveAnnouncer = inject(LiveAnnouncer);

  displayedColumns: string[] = [
    'title',
    'author',
    'id',
    'tabs',
    'published-date',
    'acciones',
  ];

  @ViewChild(MatSort)
  sort: MatSort = new MatSort();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.setupCustomSorting();
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  deleteSelected(id: number, nick: string) {
    const dialogRef = this.dialog.open(DeleteComponent, {
      width: '550px',
      data: {
        title: '¿Seguro que quieres eliminar esta cultura empresarial?',
        message: 'Se eliminarán todos los archivos y pestañas asociadas.',
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        handler: () => {
          return this.newsService.deleteCulture(id);
        },
      },
    });

    // Esperar a que el diálogo se cierre y actualizar la tabla
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.dataSource.data = this.dataSource.data.filter(
          (user) => user.id !== id
        );
      }
    });
  }

  myroles: any = {}; // Change from array to object

  ngOnInit() {
    this.loaderService.showLoader();

    // Setup navigation listener
    this.setupNavigationListener();

    // Load culture data
    this.loadCultureData();
  }

  setupNavigationListener() {
    // Clean up existing navigation subscription if it exists
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }

    // Set up new navigation subscription
    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (
          this.router.url.includes('/culture/stories/edit') ||
          this.router.url.includes('/culture/stories/add')
        ) {
          this.isUuid = true;
        } else if (this.router.url.includes('/culture/stories')) {
          this.isUuid = false;

          // Clear existing subscriptions before reloading data
          this.clearSubscriptions();

          // Reload data
          this.loadCultureData();
        }

        this.cdr.detectChanges();
      });
  }

  loadCultureData() {
    const cultureSub = this.newsService.getCulture().subscribe({
      next: (data) => {
        // console.debug('Culture data:', data);
        this.dataSource.data = data;
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        // Set up custom sorting
        this.setupCustomSorting();

        // Set up the filter predicate for date filtering
        this.setupFilterPredicate();

        // Apply initial filter
        this.applyFilters();

        // Load roles
        this.loadRoles();

        this.loaderService.hideLoader();
      },
      error: (error) => {
        console.error('Error fetching culture data:', error);
        this.loaderService.hideLoader();
      },
    });

    this.subscriptions.push(cultureSub);
  }

  setupFilterPredicate() {
    // Set up the filter predicate for date filtering
    this.dataSource.filterPredicate = (data, filter) => {
      const filterObj = JSON.parse(filter);
      let dateMatch = true;

      // Check date filter
      if (filterObj.dateFilter) {
        const createdWhen = new Date(data.created_when).getTime();
        dateMatch = createdWhen >= filterObj.dateFilter;
      }

      return dateMatch;
    };
  }

  loadRoles() {
    // Use the child identifier 'stories' directly - the service will search through all children
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'stories',
      this.storageService
    );
    // console.log('Stories permissions:', permissions);
    if (permissions) {
      this.myroles = permissions;
      // console.log('myroles set to:', this.myroles);
    } else {
      // console.log('No permissions found for stories');
    }
  }

  clearSubscriptions() {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });

    // Reset the subscriptions array
    this.subscriptions = [];
  }

  ngOnDestroy() {
    // Clean up all subscriptions when component is destroyed
    this.clearSubscriptions();

    // Also clean up navigation subscription if it exists
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
      this.navigationSubscription = null;
    }
  }

  // Set up custom sort functions for the table columns
  private setupCustomSorting() {
    this.dataSource.sortingDataAccessor = (item: any, property: string) => {
      switch (property) {
        case 'tabs':
          // Sort by number of tabs
          return item.tab_count || 0;

        case 'published-date':
          // Sort by created_when date
          return new Date(item.created_when).getTime();

        default:
          return item[property];
      }
    };
  }

  isUuid: boolean = false;
  goToCulture(element: any) {
    this.router.navigate(['home/culture/stories/edit', element.id]);
    this.isUuid = true;
  }

  goToNewCulture() {
    this.router.navigate(['home/culture/stories/add']);
    this.isUuid = true;
  }
}
